import { useState } from 'react';
import { db } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { SettingsNav } from './Branches';
import { logAudit, AUDIT_ACTIONS } from '../../utils/audit';
import { IconExternalLink } from '@tabler/icons-react';

export default function Organisation() {
  const { org, orgId, userProfile } = useAuth();

  const resolvedOrgId = orgId || userProfile?.orgId;

  const [name, setName] = useState(org?.name || '');
  const [website, setWebsite] = useState(org?.website || '');
  const [industry, setIndustry] = useState(org?.industry || 'Agricultural dealership');
  const [brandColour, setBrandColour] = useState(org?.brandColour || '#1A3A5C');
  const [faviconURL, setFaviconURL] = useState(org?.faviconURL || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'organisations', resolvedOrgId), {
        name: name.trim(),
        website: website.trim(),
        industry,
        brandColour,
        faviconURL: faviconURL.trim(),
        updatedAt: serverTimestamp(),
      });

      await logAudit(resolvedOrgId, {
        action: AUDIT_ACTIONS.ORG_SETTINGS_UPDATED,
        performedBy: userProfile?.id,
        performedByName: userProfile?.name,
        targetId: resolvedOrgId,
        targetType: 'organisation',
        targetName: name.trim(),
        details: `Organisation settings updated — name: ${name.trim()}, colour: ${brandColour}`,
        branchId: null,
      });

      document.title = name.trim() ? `Veritas — ${name.trim()}` : 'Veritas';
      if (faviconURL.trim()) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        link.href = faviconURL.trim();
        document.head.appendChild(link);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to save — ' + err.message);
    }
    setSaving(false);
  };

  const INDUSTRIES = [
    'Agricultural dealership',
    'Plant hire',
    'Logistics',
    'Construction',
    'Facilities management',
    'Other',
  ];

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <SettingsNav active="Organisation" />
        <div style={{ flex: 1, padding: 'var(--main-padding)' }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Organisation</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>Manage your organisation settings</p>

          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 18 }}>Branding</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Organisation name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Industry</label>
                <select value={industry} onChange={e => setIndustry(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Website</label>
                <input type="text" placeholder="e.g. georgebrowns.co.uk" value={website} onChange={e => setWebsite(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Brand colour</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="color" value={brandColour} onChange={e => setBrandColour(e.target.value)} style={{ width: 40, height: 36, padding: 2, border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', cursor: 'pointer', background: 'var(--color-bg-primary)' }} />
                  <input type="text" value={brandColour} onChange={e => setBrandColour(e.target.value)} style={{ flex: 1, boxSizing: 'border-box', fontFamily: 'monospace' }} />
                </div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Favicon URL</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="url" placeholder="https://yourwebsite.co.uk/favicon.ico" value={faviconURL} onChange={e => setFaviconURL(e.target.value)} style={{ flex: 1, boxSizing: 'border-box' }} />
                {faviconURL && (
                  <a href={faviconURL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand)', display: 'flex', alignItems: 'center' }}>
                    <IconExternalLink size={16} stroke={1.5} />
                  </a>
                )}
                {faviconURL && (
                  <img src={faviconURL} alt="favicon preview" style={{ width: 20, height: 20, borderRadius: 3, border: '0.5px solid var(--color-border-light)', objectFit: 'contain' }} onError={e => e.target.style.display = 'none'} />
                )}
              </div>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 5, lineHeight: 1.5 }}>
                Paste your website's favicon URL — this appears in the browser tab for your version of Veritas. Usually found at <span style={{ fontFamily: 'monospace' }}>https://yourwebsite.co.uk/favicon.ico</span>
              </div>
            </div>
          </div>

          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Data & compliance</div>
            {[
              { label: 'Data retention', value: '3 years' },
              { label: 'GDPR region', value: 'United Kingdom' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{r.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{r.value}</span>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ fontSize: 12, color: 'var(--color-danger)', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 14 }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: saved ? 'var(--color-success)' : 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, transition: 'background 0.3s' }}
          >
            {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </Layout>
  );
}